import React from "react";
import { AlertTriangle } from "lucide-react";

const MAX_LISTED = 8;

/** Whether the admin manages anyone, directly or as a department's manager. */
export const isManagingAnyone = (user) =>
  (user?.managed_employees?.length || 0) > 0 ||
  (user?.managed_departments?.length || 0) > 0;

/**
 * Warns that the admin being deactivated or deleted is still someone's
 * manager or a department's manager. Renders nothing otherwise.
 * `managed_employees` / `managed_departments` come from GET /admin/company-admins.
 */
export default function ManagedEmployeesWarning({ user, action = "deactivate" }) {
  if (!isManagingAnyone(user)) return null;

  const reports = user?.managed_employees || [];
  const departments = user?.managed_departments || [];
  const name = user?.name?.trim() || user?.email || "This user";
  const listed = reports.slice(0, MAX_LISTED);
  const hidden = reports.length - listed.length;
  const doing = action === "delete" ? "Deleting" : "Deactivating";

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
      {reports.length > 0 && (
        <>
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {name} is the manager of {reports.length} employee
            {reports.length === 1 ? "" : "s"}
          </p>
          <ul className="mt-2 list-disc space-y-0.5 pl-6">
            {listed.map((r) => (
              <li key={r.employee_id}>
                {r.name}
                {r.name !== r.employee_id ? ` (${r.employee_id})` : ""}
              </li>
            ))}
            {hidden > 0 && <li>and {hidden} more</li>}
          </ul>
        </>
      )}

      {departments.length > 0 && (
        <p className={`${reports.length > 0 ? "mt-2" : ""} flex items-start gap-2 font-semibold`}>
          {reports.length === 0 && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          {reports.length > 0 ? "Also the" : `${name} is the`} department manager of{" "}
          {departments.join(", ")}
        </p>
      )}

      <p className="mt-2">
        {doing} them removes them as manager
        {reports.length > 0 ? " from these employees" : ""}
        {departments.length > 0
          ? `${reports.length > 0 ? " and" : ""} from ${departments.length === 1 ? "that department" : "those departments"}`
          : ""}
        {action === "delete" ? "" : " (reactivating won't restore it)"}. Assign a
        new manager afterwards.
      </p>
    </div>
  );
}
